import React, { useEffect } from 'react';
import PropTypes from 'prop-types';

import { Col, Form, Row, Spinner } from 'react-bootstrap';
import Select from 'react-select';
import ToggleSwitch from 'react-switch';

import { grape } from 'lib/css/colors';
import { suggestedActionsOptions } from 'lib/enums/BrandCleanupEnums';
import Container from 'components/shared/Container';
import { GLOBAL_VENDORS_FOR_DROPDOWN } from 'lib/networking/endpoints';
import useGet from 'lib/hooks/useGet';

function BrandCleanupFilters({
  brands,
  refetchBrands,
  loadingBrands,
  suggestedBrands,
  loadingSuggestedBrands,
  distributorBrandNames,
  loadingDistributorBrandNames,
  l2Categories,
  loadingL2Categories,
  l3Categories,
  loadingL3Categories,
  l4Categories,
  loadingL4Categories,
  selectedVendor,
  setSelectedVendor,
  selectedBrand,
  setSelectedBrand,
  selectedSuggestedBrand,
  setSelectedSuggestedBrand,
  selectedDistributorBrandName,
  setSelectedDistributorBrandName,
  selectedL2Category,
  setSelectedL2Category,
  selectedL3Category,
  setSelectedL3Category,
  selectedL4Category,
  setSelectedL4Category,
  selectedSuggestedAction,
  setSelectedSuggestedAction,
  selectedSimilarityScore,
  setSelectedSimilarityScore,
  traineeWorkOnly,
  setTraineeWorkOnly,
  hasCleanupBrandsPermission,
  GMVProductsOnly,
  setGMVProductsOnly,
  minimumGMV,
  setMinimumGMV,
}) {
  const { data: { data: globalVendors } = {} } = useGet(GLOBAL_VENDORS_FOR_DROPDOWN);

  useEffect(() => {
    setSelectedBrand(null);
    refetchBrands();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVendor]);

  useEffect(() => {
    setSelectedL2Category(null);
    setSelectedL3Category(null);
    setSelectedL4Category(null);
    setSelectedDistributorBrandName(null);
    setSelectedSuggestedBrand(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedVendor,
    selectedSuggestedAction,
    selectedBrand,
    traineeWorkOnly,
    GMVProductsOnly,
    minimumGMV,
  ]);

  return (
    <Container>
      <Row>
        <Col md={2}>
          <Form.Group>
            <Form.Label>Select Distributor</Form.Label>
            <Select
              value={selectedVendor}
              options={globalVendors?.map(vendor => ({
                value: vendor.id,
                label: vendor.name,
              }))}
              onChange={e => setSelectedVendor(e)}
              placeholder="Search distributor..."
              isSearchable
              isClearable
            />
          </Form.Group>
        </Col>
        <Col md={2}>
          <Form.Group>
            <Form.Label>Suggested Action</Form.Label>
            <Select
              value={selectedSuggestedAction}
              options={suggestedActionsOptions}
              onChange={e => setSelectedSuggestedAction(e)}
              placeholder="Select Suggested Action"
              isClearable
            />
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group>
            <Form.Label>
              Current Brand (
              {loadingBrands ? (
                <Spinner animation="border" role="status" size="sm" />
              ) : (
                brands?.length || 0
              )}
              )
            </Form.Label>
            <Select
              value={selectedBrand}
              options={brands?.map(brand => ({
                value: brand.id,
                label: brand.name + ' (' + brand.product_count + ')',
              }))}
              onChange={e => setSelectedBrand(e)}
              placeholder="Select Brand"
              isLoading={loadingBrands}
              isClearable
            />
          </Form.Group>
        </Col>
        <Col md={2}>
          <Form.Group>
            <Form.Label>
              Distributor Brand Name (
              {loadingDistributorBrandNames ? (
                <Spinner animation="border" role="status" size="sm" />
              ) : (
                distributorBrandNames?.length || 0
              )}
              )
            </Form.Label>
            <Select
              value={selectedDistributorBrandName}
              options={distributorBrandNames?.map(distributorBrandName => ({
                value: distributorBrandName.id,
                label: distributorBrandName.name + ' (' + distributorBrandName.product_count + ')',
              }))}
              onChange={e => setSelectedDistributorBrandName(e)}
              placeholder="Distributor Brand Name"
              isLoading={loadingDistributorBrandNames}
              isClearable
            />
          </Form.Group>
        </Col>
        <Col md={3}>
          <Form.Group>
            <Form.Label>
              Suggested Brand (
              {loadingSuggestedBrands ? (
                <Spinner animation="border" role="status" size="sm" />
              ) : (
                suggestedBrands?.length || 0
              )}
              )
            </Form.Label>
            <Select
              value={selectedSuggestedBrand}
              options={suggestedBrands?.map(brand => ({
                value: brand.id,
                label: brand.name + ' (' + brand.product_count + ')',
              }))}
              onChange={e => setSelectedSuggestedBrand(e)}
              placeholder="Select Brand"
              isLoading={loadingSuggestedBrands}
              isClearable
            />
          </Form.Group>
        </Col>
      </Row>
      <Row className="align-items-end">
        <Col md={2}>
          <Form.Group>
            <Form.Label>
              L2 Category (
              {loadingL2Categories ? (
                <Spinner animation="border" role="status" size="sm" />
              ) : (
                l2Categories?.length || 0
              )}
              )
            </Form.Label>
            <Select
              value={selectedL2Category}
              options={l2Categories?.map(l2Category => ({
                value: l2Category.id,
                label: l2Category.name + ' (' + l2Category.product_count + ')',
              }))}
              onChange={e => setSelectedL2Category(e)}
              placeholder="Select L2"
              isLoading={loadingL2Categories}
              isClearable
            />
          </Form.Group>
        </Col>
        <Col md={2}>
          <Form.Group>
            <Form.Label>
              L3 Category (
              {loadingL3Categories ? (
                <Spinner animation="border" role="status" size="sm" />
              ) : (
                l3Categories?.length || 0
              )}
              )
            </Form.Label>
            <Select
              value={selectedL3Category}
              options={l3Categories?.map(l3Category => ({
                value: l3Category.id,
                label: l3Category.name + ' (' + l3Category.product_count + ')',
              }))}
              onChange={e => setSelectedL3Category(e)}
              placeholder="Select L3"
              isLoading={loadingL3Categories}
              isClearable
            />
          </Form.Group>
        </Col>
        <Col md={2}>
          <Form.Group>
            <Form.Label>
              L4 Category (
              {loadingL4Categories ? (
                <Spinner animation="border" role="status" size="sm" />
              ) : (
                l4Categories?.length || 0
              )}
              )
            </Form.Label>
            <Select
              value={selectedL4Category}
              options={l4Categories?.map(l4Category => ({
                value: l4Category.id,
                label: l4Category.name + ' (' + l4Category.product_count + ')',
              }))}
              onChange={e => setSelectedL4Category(e)}
              placeholder="Select L4"
              isLoading={loadingL4Categories}
              isClearable
            />
          </Form.Group>
        </Col>
        <Col md={2}>
          <Form.Group>
            <Form.Label>Min Confidence Score</Form.Label>
            <Form.Control
              type="number"
              placeholder="0.0 - 1.0"
              min="0"
              max="1"
              step="0.05"
              value={selectedSimilarityScore || ''}
              onChange={e => {
                const value = e.target.value;
                setSelectedSimilarityScore(value ? parseFloat(value) : null);
              }}
            />
          </Form.Group>
        </Col>
        {GMVProductsOnly && (
          <Col md={2}>
            <Form.Group>
              <Form.Label>Minimum GMV</Form.Label>
              <Form.Control
                type="number"
                placeholder="0.0 - 1.0"
                min="0"
                max="1000000000"
                step="100000"
                value={minimumGMV || ''}
                onChange={e => {
                  const value = e.target.value;
                  setMinimumGMV(value ? parseFloat(value) : null);
                }}
              />
            </Form.Group>
          </Col>
        )}
        <Col md={1}>
          <Form.Group className="d-flex flex-column align-items-start mb-2">
            <Form.Label>GMV Products Only</Form.Label>
            <ToggleSwitch
              onColor={grape}
              checked={GMVProductsOnly}
              checkedIcon={false}
              uncheckedIcon={false}
              onChange={() => setGMVProductsOnly(!GMVProductsOnly)}
            />
          </Form.Group>
        </Col>
        {hasCleanupBrandsPermission && (
          <Col md={1}>
            <Form.Group className="d-flex flex-column align-items-start mb-2">
              <Form.Label>Trainee work Only</Form.Label>
              <ToggleSwitch
                onColor={grape}
                checked={traineeWorkOnly}
                checkedIcon={false}
                uncheckedIcon={false}
                onChange={() => {
                  const newValue = !traineeWorkOnly;
                  setTraineeWorkOnly(newValue);
                }}
              />
            </Form.Group>
          </Col>
        )}
      </Row>
    </Container>
  );
}

BrandCleanupFilters.propTypes = {
  brands: PropTypes.array,
  refetchBrands: PropTypes.func,
  loadingBrands: PropTypes.bool,
  suggestedBrands: PropTypes.array,
  loadingSuggestedBrands: PropTypes.bool,
  distributorBrandNames: PropTypes.array,
  loadingDistributorBrandNames: PropTypes.bool,
  l2Categories: PropTypes.array,
  loadingL2Categories: PropTypes.bool,
  l3Categories: PropTypes.array,
  loadingL3Categories: PropTypes.bool,
  l4Categories: PropTypes.array,
  loadingL4Categories: PropTypes.bool,
  selectedVendor: PropTypes.object,
  setSelectedVendor: PropTypes.func,
  selectedBrand: PropTypes.object,
  setSelectedBrand: PropTypes.func,
  selectedSuggestedBrand: PropTypes.object,
  setSelectedSuggestedBrand: PropTypes.func,
  selectedDistributorBrandName: PropTypes.object,
  setSelectedDistributorBrandName: PropTypes.func,
  selectedL2Category: PropTypes.object,
  setSelectedL2Category: PropTypes.func,
  selectedL3Category: PropTypes.object,
  setSelectedL3Category: PropTypes.func,
  selectedL4Category: PropTypes.object,
  setSelectedL4Category: PropTypes.func,
  selectedSuggestedAction: PropTypes.object,
  setSelectedSuggestedAction: PropTypes.func,
  selectedSimilarityScore: PropTypes.number,
  setSelectedSimilarityScore: PropTypes.func,
  isWrongBrand: PropTypes.bool,
  setIsWrongBrand: PropTypes.func,
  isWrongManufacturer: PropTypes.bool,
  setIsWrongManufacturer: PropTypes.func,
  traineeWorkOnly: PropTypes.bool,
  setTraineeWorkOnly: PropTypes.func,
  hasCleanupBrandsPermission: PropTypes.bool,
  GMVProductsOnly: PropTypes.bool,
  setGMVProductsOnly: PropTypes.func,
  minimumGMV: PropTypes.number,
  setMinimumGMV: PropTypes.func,
};

export default BrandCleanupFilters;
